<script setup lang="ts">
import { StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { channelColumns, groupByType } from '../internal/preferences.ts'
import type { NotificationChannel, NotificationPreferences, NotificationType } from '../internal/types.ts'

const props = defineProps<{
  preferences: NotificationPreferences
  isSaving: boolean
}>()

const emit = defineEmits<{
  toggleChannel: [type: NotificationType, channel: NotificationChannel, enabled: boolean]
  toggleShowAmounts: [channel: NotificationChannel, enabled: boolean]
}>()

const { t } = useI18n()

const columns = computed(() => channelColumns(props.preferences.preferences))
const rows = computed(() => groupByType(props.preferences.preferences))
</script>

<template>
  <div class="flex flex-col gap-[var(--space-block)]">
    <div class="flex flex-wrap gap-[var(--space-block)]">
      <label
        v-for="channel in columns"
        :key="channel"
        class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
      >
        <input
          :checked="preferences.showAmounts[channel]"
          type="checkbox"
          :disabled="isSaving"
          @change="emit('toggleShowAmounts', channel, ($event.target as HTMLInputElement).checked)"
        />
        {{
          t('notificationPreferences.showAmounts', {
            channel: t(`notificationPreferences.channel.${channel}`),
          })
        }}
      </label>
    </div>

    <table class="w-full border-collapse text-[length:var(--text-step-2)]">
      <thead>
        <tr class="table-head">
          <th class="p-[var(--space-cell)]">
            {{ t('notificationPreferences.typeColumn') }}
          </th>
          <th v-for="channel in columns" :key="channel" class="p-[var(--space-cell)]">
            {{ t(`notificationPreferences.channel.${channel}`) }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.type" class="border-b border-[color:var(--color-border)]">
          <td class="p-[var(--space-cell)] text-[color:var(--color-text)]">
            {{ t(`notificationPreferences.type.${row.type}`) }}
          </td>
          <td v-for="channel in columns" :key="channel" class="p-[var(--space-cell)]">
            <span v-if="row.cells[channel] === undefined" class="text-[color:var(--color-text-muted)]"
              >—</span
            >
            <div v-else class="flex items-center gap-[var(--space-inline)]">
              <input
                :checked="row.cells[channel]!.enabled"
                type="checkbox"
                :disabled="isSaving || row.cells[channel]!.mandatory"
                :aria-label="
                  t('notificationPreferences.toggleLabel', {
                    type: t(`notificationPreferences.type.${row.type}`),
                    channel: t(`notificationPreferences.channel.${channel}`),
                  })
                "
                @change="
                  emit('toggleChannel', row.type, channel, ($event.target as HTMLInputElement).checked)
                "
              />
              <StatusTag v-if="row.cells[channel]!.mandatory" tone="neutral" size="sm">
                {{ t('notificationPreferences.mandatory') }}
              </StatusTag>
              <StatusTag v-else-if="row.cells[channel]!.source === 'OVERRIDE'" tone="wait" size="sm">
                {{ t('notificationPreferences.customized') }}
              </StatusTag>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
